// chat-story Remotion composition：对话叙事，消息逐条弹入（每 2.5s 一条）。
// 复用 ./tick 的 chatReveal 推导已揭示消息数。sender=0 视为"我"（右侧），其余左侧。
// 布局用 flex column + justify-end：气泡 width:fit-content 自动尺寸（浏览器算高度），
// 不再手算高度——避免 CJK 字宽估算偏差导致气泡重叠。超屏时顶部裁剪、最新在底部。
import { AbsoluteFill, useCurrentFrame, useVideoConfig, interpolate } from 'remotion'
import { chatReveal } from './tick'
import type { ChatData } from './types'
import type { CompositionProps } from '../../remotion/ScenePlayer'
import { THEME } from '../../remotion/theme'

const PAD = 48
const BUBBLE_MAX = 720
const AVATAR = 80
const FS = 42
const TITLE_H = 150

export const ChatStoryComposition: React.FC<CompositionProps> = ({ data }) => {
  const frame = useCurrentFrame()
  const { fps } = useVideoConfig()
  const d = data as unknown as ChatData
  const time = frame / fps
  const { revealCount } = chatReveal(data, time)
  const messages = (d.messages || []).slice(0, revealCount)

  return (
    <AbsoluteFill style={{ background: (data as any).bgColor || THEME.bg, fontFamily: THEME.fontSans }}>
      {/* 标题栏 */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: TITLE_H, padding: `0 ${PAD}px`,
        display: 'flex', alignItems: 'center', borderBottom: `2px solid ${THEME.border}`,
      }}>
        <div style={{ color: THEME.w, fontSize: 48, fontWeight: 700 }}>{d.title || 'Chat'}</div>
      </div>

      {/* 消息区：flex 列、底对齐、溢出裁顶（最新恒在底部） */}
      <div style={{
        position: 'absolute', top: TITLE_H, left: 0, right: 0, bottom: 0, padding: PAD,
        display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', overflow: 'hidden',
      }}>
        {messages.map((m, i) => {
          const isSelf = m.sender === 0
          // 揭示前 0.35s 淡入、揭示时刻完成（frame 0 的首条可见）
          const enter = interpolate(time - i * 2.5, [-0.35, 0], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' })
          return (
            <div key={i} style={{
              display: 'flex', flexDirection: isSelf ? 'row-reverse' : 'row',
              alignItems: 'flex-start', gap: 18, marginBottom: 18, opacity: enter,
              transform: `translateY(${(1 - enter) * 16}px)`,
            }}>
              <div style={{
                flex: '0 0 auto', width: AVATAR, height: AVATAR, borderRadius: '50%',
                background: isSelf ? THEME.p : THEME.bg3, color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32, fontWeight: 700,
              }}>
                {(d.participants?.[m.sender] || '?').slice(0, 1)}
              </div>
              <div style={{
                width: 'fit-content', maxWidth: BUBBLE_MAX,
                background: isSelf ? THEME.p : THEME.bg3, color: '#fff', borderRadius: 24,
                padding: '18px 28px', fontSize: FS, lineHeight: 1.4, fontWeight: 500,
                borderBottomRightRadius: isSelf ? 6 : 24, borderBottomLeftRadius: isSelf ? 24 : 6,
                whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              }}>
                {m.text}
              </div>
            </div>
          )
        })}
      </div>
    </AbsoluteFill>
  )
}
